export const AZURE_OAUTH_ACCOUNT_ID_KEY = 'azure_oauth_account_id'
export const AZURE_OAUTH_AUTH_URI_KEY = 'azure_oauth_auth_uri'

export const getAzureOauthCacheKey = (partnerId: string) =>
  `azureOauth:${partnerId}`

export const setAzureOauthAccountId = (accountId: string) => {
  sessionStorage.setItem(AZURE_OAUTH_ACCOUNT_ID_KEY, accountId)
}

export const getAzureOauthAccountId = (): string | null =>
  sessionStorage.getItem(AZURE_OAUTH_ACCOUNT_ID_KEY)

export const clearAzureOauthAccountId = () => {
  sessionStorage.removeItem(AZURE_OAUTH_ACCOUNT_ID_KEY)
}

/** Persist user-login auth URI so admin-consent callback can open step 2 without Redux. */
export const setAzureOauthAuthUri = (authUri: string) => {
  sessionStorage.setItem(AZURE_OAUTH_AUTH_URI_KEY, authUri)
}

export const getAzureOauthAuthUri = (): string | null =>
  sessionStorage.getItem(AZURE_OAUTH_AUTH_URI_KEY)

export const clearAzureOauthAuthUri = () => {
  sessionStorage.removeItem(AZURE_OAUTH_AUTH_URI_KEY)
}

export const clearAzureOauthSession = () => {
  clearAzureOauthAccountId()
  clearAzureOauthAuthUri()
}

export const clearAzureOauthCache = (partnerId: string) => {
  localStorage.removeItem(getAzureOauthCacheKey(partnerId))
}

export const ADMIN_CONSENT_REQUIRED_MESSAGE =
  'A Microsoft admin must approve Labra for your organization before connecting.'

export const ADMIN_CONSENT_MISSING_AUTH_URI_MESSAGE =
  'Admin consent succeeded, but the sign-in link was missing. Please try Connect again.'

/** Normalize popup callback `params` (with or without leading `?`) into URLSearchParams. */
export const parseAzureOauthCallbackParams = (
  params: unknown
): URLSearchParams => {
  const raw =
    typeof params === 'string'
      ? params.startsWith('?')
        ? params.slice(1)
        : params
      : ''
  return new URLSearchParams(raw)
}

export type AdminConsentCallbackResult =
  | { status: 'not_admin_consent' }
  | { status: 'error'; message: string }
  | { status: 'missing_auth_uri'; message: string }
  | { status: 'continue'; authUri: string }

/**
 * Shared admin-consent step used by ConnectToMpcButton and AzurePopupConnection.
 * Returns how the opener should proceed after the consent popup posts back.
 */
export const resolveAdminConsentCallback = (
  callbackParams: URLSearchParams,
  fallbackAuthUri?: string | null
): AdminConsentCallbackResult => {
  if (callbackParams.get('admin_consent')?.toLowerCase() !== 'true') {
    return { status: 'not_admin_consent' }
  }

  const consentError = callbackParams.get('error') || ''
  if (consentError) {
    return {
      status: 'error',
      message:
        callbackParams.get('error_description') ||
        ADMIN_CONSENT_REQUIRED_MESSAGE,
    }
  }

  const nextAuthUri = getAzureOauthAuthUri() || fallbackAuthUri || ''
  if (!nextAuthUri) {
    return {
      status: 'missing_auth_uri',
      message: ADMIN_CONSENT_MISSING_AUTH_URI_MESSAGE,
    }
  }

  return { status: 'continue', authUri: nextAuthUri }
}

export const buildAzureOauthReturnPath = ({
  productType,
  accountId,
  queryString,
  isOnboardingRoute = false,
}: {
  productType: string
  accountId?: string
  queryString?: string
  isOnboardingRoute?: boolean
}): string => {
  const suffix = queryString ? `?${queryString}` : ''
  if (isOnboardingRoute) {
    return `/${productType}/onboarding/cloud-settings${suffix}`
  }
  if (accountId) {
    return `/settings/${productType}/cloud/azure/${accountId}${suffix}`
  }
  return `/settings/${productType}/cloud/azure${suffix}`
}

export const pickConsentOrAuthUri = (
  oauthUrls?: {
    adminConsentUri?: string | null
    authUri?: string | null
  } | null
): string | null => oauthUrls?.adminConsentUri || oauthUrls?.authUri || null

/** sessionStorage lock so remounts/reloads cannot double-POST the same code:state */
const getAzureOauthCallbackLockKey = (callbackKey: string) =>
  `azureOauthCallback:${callbackKey}`

/**
 * Claim a code:state pair for processing. Returns false if already pending/done
 * (survives Strict Mode remounts and success reloads).
 */
export const tryClaimAzureOauthCallback = (callbackKey: string): boolean => {
  const key = getAzureOauthCallbackLockKey(callbackKey)
  if (sessionStorage.getItem(key)) {
    return false
  }
  sessionStorage.setItem(key, 'pending')
  return true
}

/** Mark exchange succeeded — blocks any further POSTs for this code:state */
export const completeAzureOauthCallback = (callbackKey: string) => {
  sessionStorage.setItem(getAzureOauthCallbackLockKey(callbackKey), 'done')
}

/**
 * Release after a failed/transient exchange so the same callback URL can be retried.
 */
export const releaseAzureOauthCallback = (callbackKey: string) => {
  sessionStorage.removeItem(getAzureOauthCallbackLockKey(callbackKey))
}
